import api from '../../../api/client';
import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  message,
  Popconfirm,
  Modal,
  Form,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { usePermissions } from "../../../permissions";
import config from "../../../config";

const { Text } = Typography;

const PermissionManager = () => {
  const [permissions, setPermissions] = useState([]);
  const [filteredPermissions, setFilteredPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const [form] = Form.useForm();
  const { createPermission, editPermission, deletePermission } = usePermissions();
  const environment = window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/permissions`);
      setPermissions(data);
      setFilteredPermissions(data);
    } catch (err) {
      message.error("Failed to load permissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  useEffect(() => {
    if (searchText) {
      const filtered = permissions.filter((p) =>
        p.name?.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredPermissions(filtered);
    } else {
      setFilteredPermissions(permissions);
    }
  }, [searchText, permissions]);

  const handleSave = async (values) => {
    try {
      if (editingPermission) {
        await api.put(`/permissions/${editingPermission.id}`, values);
        message.success("Permission updated");
      } else {
        await api.post(`/permissions`, values);
        message.success("Permission created");
      }
      setModalVisible(false);
      setEditingPermission(null);
      form.resetFields();
      fetchPermissions();
    } catch (err) {
      message.error(err.response?.data?.message || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/permissions/${id}`);
      message.success("Permission deleted");
      fetchPermissions();
    } catch (err) {
      message.error("Delete failed - permission may be in use");
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Module Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => new Date(date).toLocaleString(),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space>
          {editPermission && (
            <Tooltip title="Edit Module">
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => {
                  setEditingPermission(record);
                  form.setFieldsValue({ name: record.name });
                  setModalVisible(true);
                }}
              />
            </Tooltip>
          )}
          {deletePermission && (
            <Popconfirm
              title="Delete this module?"
              description="This will remove the permission from all roles."
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Delete Module">
                <Button icon={<DeleteOutlined />} size="small" danger />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Permission Modules"
      extra={
        <Space>
          <Input.Search
            placeholder="Search modules..."
            allowClear
            style={{ width: 250 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchOutlined />}
          />
          {searchText && (
            <Button
              size="small"
              onClick={() => setSearchText("")}
              icon={<ClearOutlined />}
            >
              Clear
            </Button>
          )}
          <Button icon={<ReloadOutlined />} onClick={fetchPermissions}>
            Refresh
          </Button>
          {createPermission && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingPermission(null);
                form.resetFields();
                setModalVisible(true);
              }}
            >
              Add Module
            </Button>
          )}
        </Space>
      }
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Total Modules"
              value={permissions.length}
              valueStyle={{ color: "#1677ff" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Active Modules"
              value={permissions.length}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={filteredPermissions}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: "max-content" }}
        size="small"
      />

      <Modal
        title={editingPermission ? "Edit Module" : "Add Module"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingPermission(null);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="name"
            label="Module Name"
            rules={[
              { required: true, message: "Please enter module name" },
              { min: 2, message: "Module name must be at least 2 characters" },
            ]}
          >
            <Input placeholder="e.g., Users, Roles, Inventory, Sales, Purchases" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingPermission ? "Update" : "Create"}
              </Button>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default PermissionManager;